import { forwardRef } from 'react'
import { cn } from '@/lib/utils/cn'

export interface ToggleProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: React.ReactNode
  /** Render the label to the left of the track instead of the right. */
  labelPosition?: 'left' | 'right'
}

export const Toggle = forwardRef<HTMLInputElement, ToggleProps>(function Toggle(
  { label, labelPosition = 'right', className, disabled, ...rest },
  ref,
) {
  const track = (
    <span className="relative inline-flex h-[18px] w-8 flex-shrink-0 items-center">
      <input
        ref={ref}
        type="checkbox"
        role="switch"
        disabled={disabled}
        className="peer absolute inset-0 h-full w-full cursor-inherit opacity-0"
        {...rest}
      />
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute inset-0 rounded-full bg-muted transition-colors duration-fast',
          'peer-checked:bg-accent',
          'peer-focus-visible:shadow-focus',
          'peer-disabled:opacity-50',
        )}
      />
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute left-0.5 top-0.5 h-[14px] w-[14px] rounded-full bg-surface shadow-sm transition-transform duration-fast',
          'peer-checked:translate-x-[14px]',
        )}
      />
    </span>
  )

  return (
    <label
      className={cn(
        'inline-flex cursor-pointer select-none items-center gap-2.5 text-md',
        disabled && 'cursor-not-allowed text-text-disabled',
        className,
      )}
    >
      {label && labelPosition === 'left' ? <span className="text-primary">{label}</span> : null}
      {track}
      {label && labelPosition === 'right' ? <span className="text-primary">{label}</span> : null}
    </label>
  )
})
